import { AxiosResponse } from 'axios';
import qs from 'qs';
import QualityObservations from '~/@core/domain/model/order/QualityObservations';
import ProductCategory from '~/@core/domain/model/product/ProductCategory';
import Stocks from '~/@core/domain/model/stock/Stocks';
import paramsSerializer from '~/config/axios/paramSerializer';
import { PermissionNames } from '~/constants/permissionNames';
import { Expedition, ProductDetailed } from '~/domain/api';
import { ReservationStatus } from '~/domain/enum/klokReservation/reservationStatus.enum';
import { RecipientDetailed } from '~/domain/model';
import { ISiteIdParams } from '~/domain/model/KlokReservation/KlokReservationService';
import ProductGroupWithCategories from '~/domain/model/KlokReservation/ProductGroupWithCategories';
import api, { apiGfp } from '~/services/api';
import { IKlokReservationTableData } from '~/zustand/gfp/gfp-service-state';
import { ITableData, ITableHistory } from '~/zustand/klokReservation/useKlokReservationStore';

type Params = {
  [key: string]: unknown;
};

const paramsSerializerFormatted = <T extends Params>(params: T) =>
  qs.stringify(params, { arrayFormat: 'repeat', skipNulls: true });

export const getProductsDetailed = (params: ISiteIdParams) => {
  const { siteIds } = params;

  return api.get<void, AxiosResponse<ProductDetailed[]>>(`/product/site/${siteIds}/detailed`);
};

export const getProductCategories = (params: ISiteIdParams) => {
  const queryParams = {
    ...params,
    permissionName: PermissionNames.Orders.MenuKlokReservation,
  };

  return api.get<void, AxiosResponse<ProductCategory[]>>(`/product-category/by-site-ids`, {
    params: queryParams,
    paramsSerializer: paramsSerializerFormatted,
  });
};

export const getProductGroups = (params: ISiteIdParams) => {
  const { siteIds } = params;

  return api.get<void, AxiosResponse<ProductGroupWithCategories[]>>(
    `/product-group/site/${siteIds}`,
  );
};

export const getProductRecipients = (params: ISiteIdParams) => {
  return api.get<void, AxiosResponse<RecipientDetailed[]>>('/product/recipients/by-site-ids', {
    params,
    paramsSerializer: paramsSerializerFormatted,
  });
};

export const getExpedition = (siteId: number) => {
  return api.get<void, AxiosResponse<Expedition[]>>(`/expedition/${siteId}`);
};

export interface IResponseTable extends AxiosResponse {
  data: {
    totalCount: number;
    results: Stocks[];
  };
}

export interface IParamsTable {
  PageSize: number;
  PageNumber: number;
  Data: {
    SiteId?: number | null;
    DeliveryDate?: string | null;
    ReservedStatusId?: ReservationStatus | null;
    CategoryId?: number | null;
    GroupId?: number | null;
    ProductId?: number | null;
    Size?: number | null;
    QualityId?: number | null;
    ExpeditionId?: number | null;
    PublishedBy?: string | null;
  };
}

export interface IResponseKlokReservationTable extends AxiosResponse {
  data: {
    totalCount: number;
    results: ITableData[];
  };
}

export async function getTable(params: IParamsTable): Promise<IResponseKlokReservationTable> {
  return apiGfp.get('klokReservation/klok-order', {
    params,
    paramsSerializer,
  });
}

export interface IParamsTableKlokOrder {
  PageSize: number;
  PageNumber: number;
  Data: {
    SiteId?: number | null;
    DeliveryDate?: string | null;
    CommercialDate?: string | null;
  };
}

export interface IResponseKlokReservationTableGfp extends AxiosResponse {
  data: {
    totalCount: number;
    results: IKlokReservationTableData[];
  };
}

export async function getTableKlokReservationGfp(
  params: IParamsTableKlokOrder,
): Promise<IResponseKlokReservationTableGfp> {
  return apiGfp.get('klokReservation/klok-order/gfp', {
    params,
    paramsSerializer,
  });
}

export interface IFetchStocksParams {
  SiteId: number;
  FirstWeekDay: string;
  LastWeekDay: string;
  Keyword?: string;
}
export async function fetchStocks(params: IFetchStocksParams): Promise<IResponseTable> {
  return api.get('/stocks', {
    params,
  });
}

export interface IResponseQualityObservations extends AxiosResponse {
  data: {
    totalCount: number;
    result: QualityObservations[];
  };
}

export async function fetchqualityObservations(): Promise<IResponseQualityObservations> {
  return api.get('/order/quality-observation');
}

export interface ICreateKlokReservationItems {
  stockId: number;
  packingId: number;
  quantityPacking: number;
  quantityPerPacking: number;
  observation: string;
  qualityObservation1: number;
  qualityObservation2: number;
}
export interface ICreateKlokReservationParams {
  billingDate: string;
  deliveryDate: string;
  items: ICreateKlokReservationItems[];
}

export async function createKlokReservation(params: ICreateKlokReservationParams): Promise<void> {
  return apiGfp.post('/klokReservation', params, { paramsSerializer });
}

export async function cancelKlokReservation(guid: string): Promise<void> {
  return apiGfp.patch(`/klokReservation/klok-order/cancel/${guid}`);
}

export interface IFetchKlokOrderHistory {
  PageSize: number;
  PageNumber: number;
  Data: {
    klokReservationId: number;
  };
}

export interface IResponseHistoryTable extends AxiosResponse {
  data: {
    totalCount: number;
    results: ITableHistory[];
  };
}

export async function fetchKlokOrderHistory(
  params: IFetchKlokOrderHistory,
): Promise<IResponseHistoryTable> {
  return apiGfp.get('/klokReservation/klok-order-histories', {
    params,
    paramsSerializer,
  });
}

export interface IUpdateKlokReservationParams {
  klokReservationId: number;
  guid?: string;
  stockId: number;
  deliveryDate: string;
  billingDate: string;
  natureOperationId: number;
  packingId: number;
  quantityPacking: number;
  quantityPerPacking: number;
  obs1Id?: number;
  obs2Id?: number;
  observation?: string;
}

export async function updateKlokReservation(params: IUpdateKlokReservationParams): Promise<void> {
  return apiGfp.put('/klokReservation/klok-order', params, {
    paramsSerializer,
  });
}
